import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CONTACT } from "@/config/contact";
import { useI18n } from "@/i18n/i18n";
import LanguageToggle from "@/components/site/LanguageToggle";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { tr } = useI18n();

  const links = [
    { to: "/", label: tr.nav.home },
    { to: "/servicios", label: tr.nav.services },
    { to: "/nosotros", label: tr.nav.about },
    { to: "/testimonios", label: tr.nav.testimonials },
    { to: "/contacto", label: tr.nav.contact },
  ];

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src={CONTACT.logoIconUrl}
            alt=""
            loading="eager"
            decoding="async"
            className="h-14 w-14 sm:h-16 sm:w-16 object-contain pointer-events-none group-hover:scale-105 transition-transform"
            style={{
              mixBlendMode: "screen",
              filter: "brightness(1.2) contrast(2.2) saturate(1.25)",
              WebkitMaskImage:
                "radial-gradient(circle at 50% 50%, #000 45%, rgba(0,0,0,0.7) 60%, transparent 78%)",
              maskImage:
                "radial-gradient(circle at 50% 50%, #000 45%, rgba(0,0,0,0.7) 60%, transparent 78%)",
            }}
          />
          <span className="hidden sm:inline font-heading text-lg font-semibold text-white tracking-tight whitespace-nowrap">
            Master Credit <span className="text-[color:var(--mc-cyan)]">Group</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[color:var(--mc-cyan)]"
                    : "text-white/70 hover:text-[color:var(--mc-cyan)]"
                }`
              }
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          <Link to="/contacto" className="btn-primary text-sm" data-testid="nav-cta-consulta">
            {tr.nav.cta_consult}
          </Link>
        </div>

        <button
          className="lg:hidden h-11 w-11 rounded-full border border-white/15 bg-white/5 text-white flex items-center justify-center"
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-[color:var(--mc-bg-2)]"
            data-testid="nav-mobile-panel"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-base font-medium ${
                      isActive ? "text-[color:var(--mc-cyan)]" : "text-white/85"
                    }`
                  }
                  data-testid={`nav-mobile-link-${l.to.replace("/", "") || "home"}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-3 mt-1">
                <LanguageToggle compact />
                <Link
                  to="/contacto"
                  onClick={() => setOpen(false)}
                  className="btn-primary flex-1 justify-center"
                  data-testid="nav-mobile-cta"
                >
                  {tr.nav.cta_consult}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
